import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Color, MODAL_FOOTER_BUTTON_SIZE, MODAL_PRIMARY_COLOR, assertFooterShape, t, type DestructiveSubject, type FooterActionKind } from '@bms2/shared';
import { Button } from '../controls';
import { reportCaughtInBrowser } from '../../shell/systemErrors';
import type { ReversibleConfirm } from '../controls/Button';
import { ModalCancel, ModalShell } from './ModalShell';
import styles from './modal.module.css';

/**
 * An ordinary modal: the shell, plus the Footer contract (piece 5).
 *
 * The Footer holds a Primary, a Cancel and at most one destructive-outlined
 * secondary action, and the shape is asserted rather than trusted. The Primary
 * is navy in every modal, which overrides the specification's filled red
 * button: red is this application's destructive cue, and the claim that the
 * destructive dialog's red header is the one place a strong colour cue belongs
 * holds only while ordinary modal Primaries stay navy.
 *
 * A Footer action marked destructive routes through the Destructive Action
 * Confirmation Dialog, because the Button does that itself (decision 0093).
 */
export const modalDocumentedStates = ['open', 'scrolling', 'destructiveFooter', 'stacked'] as const;

export interface ModalFooterAction {
  kind: FooterActionKind;
  label: string;
  /**
   * 🔒 The act may return the promise of its write (decision 2840): the footer
   * disables the Primary and holds the `×`, Cancel and Escape until it settles,
   * so a second press cannot send a second write. Returning nothing keeps the
   * old behaviour, a window that guards itself.
   */
  onAct: () => unknown;
  disabled?: boolean;
  /**
   * 🔒 Why the action is greyed, handed to its Button so a disabled footer
   * button says why, as decision 0559 asks of every disabled button (piece 5).
   */
  disabledReason?: string;
  /** The typed phrase the destructive gate asks for, when this is that action; `toTrash` where the delete goes to the Trash (decision 12970). */
  destructive?: DestructiveSubject;
  /** 🔨 A reversible destructive action confirms plainly instead (decision 0262). */
  confirm?: ReversibleConfirm;
  /**
   * The Primary's further choices, which make it a split button (piece 5): the
   * chevron offers them and choosing one re-arms the main zone, as every split
   * Button does. Pay and Print Now and Pay and Print Later are the first
   * (decision 1131).
   */
  more?: readonly { label: string; onAct: () => unknown }[];
}

export interface ModalProps {
  open: boolean;
  title: string;
  subtitle?: string;
  onCancel: () => void;
  actions: readonly ModalFooterAction[];
  children: ReactNode;
  width?: 'regular' | 'wide' | 'document';
  /** 🔨 `tall` is the whole screen's height, for a modal whose body is a document read beside its fields (decisions 0563, 1185). */
  height?: 'content' | 'tall';
  openerId?: string | null;
  dataModal?: string;
  /** 🔒 Decision 2641: a close-only window may close on a backdrop press; a window with a Cancel never does. */
  backdropCloses?: boolean;
}

/** The reason a greyed footer action carries, spread onto its Button only when it has one. */
function reasonOf(action: ModalFooterAction): { disabledReason?: string } {
  return action.disabledReason === undefined ? {} : { disabledReason: action.disabledReason };
}

export function Modal({ open, title, subtitle, onCancel, actions, children, width = 'regular', height = 'content', openerId = null, dataModal = 'shell', backdropCloses = false }: ModalProps) {
  assertFooterShape(actions.map((a) => a.kind));
  const closeOnly = actions.find((a) => a.kind === 'close');
  const primary = actions.find((a) => a.kind === 'primary')!;
  const cancel = actions.find((a) => a.kind === 'cancel')!;
  const secondary = actions.find((a) => a.kind === 'destructiveSecondary');
  // 🔒 A Primary whose act returned a promise stays pressed until it settles (decision 2840, T2102).
  const [settling, setSettling] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const settles = (act: () => unknown) => () => {
    const outcome = act();
    if (outcome && typeof (outcome as PromiseLike<unknown>).then === 'function') {
      setSettling(true);
      const done = () => {
        if (mounted.current) setSettling(false);
      };
      // 🔒 A rejected write re-arms the Primary and is then reported, as the unhandled rejection it replaces was (decisions 0822, 2200).
      (outcome as PromiseLike<unknown>).then(done, (error: unknown) => {
        done();
        reportCaughtInBrowser('Modal.footerPrimary', error);
      });
    }
  };
  // 🔒 A disabled Cancel holds the `×` and Escape with it: the three ways out are one outcome (piece 5).
  const held = settling || (closeOnly ? (closeOnly.disabled ?? false) : (cancel.disabled ?? false));

  // 🔒 Decision 2140: a window that writes nothing draws one Close, the same close the `×` and Escape call.
  const footer = closeOnly ? (
    <Button size={MODAL_FOOTER_BUTTON_SIZE} color={MODAL_PRIMARY_COLOR} text={[closeOnly.label]} actions={[closeOnly.onAct]} disabled={held} />
  ) : (
    <>
      {secondary ? (
        <span className={styles.secondary}>
          {secondary.confirm ? (
            <Button
              size={MODAL_FOOTER_BUTTON_SIZE}
              color={Color.Destructive}
              confirm={secondary.confirm}
              text={[secondary.label]}
              actions={[secondary.onAct]}
              disabled={secondary.disabled ?? false}
              {...reasonOf(secondary)}
            />
          ) : (
            <Button
              size={MODAL_FOOTER_BUTTON_SIZE}
              color={Color.Destructive}
              destructive={secondary.destructive ?? { subject: secondary.label, phrase: t('modal.deletePhrase') }}
              text={[secondary.label]}
              actions={[secondary.onAct]}
              disabled={secondary.disabled ?? false}
              {...reasonOf(secondary)}
            />
          )}
        </span>
      ) : null}
      <ModalCancel label={cancel.label} onCancel={cancel.onAct} disabled={held} />
      <Button
        size={MODAL_FOOTER_BUTTON_SIZE}
        color={MODAL_PRIMARY_COLOR}
        text={[primary.label, ...(primary.more ?? []).map((choice) => choice.label)]}
        actions={[settles(primary.onAct), ...(primary.more ?? []).map((choice) => settles(choice.onAct))]}
        disabled={settling || (primary.disabled ?? false)}
        {...reasonOf(primary)}
      />
    </>
  );

  const shellProps = { open, title, onCancel, held, footer, width, height, openerId, dataModal, backdropCloses: backdropCloses && closeOnly !== undefined, ...(subtitle === undefined ? {} : { subtitle }) };
  return <ModalShell {...shellProps}>{children}</ModalShell>;
}

export { ModalField } from './ModalShell';
